import { callExternalApi } from "./external-api.service";
import { createTask, storeTask, TASK_TYPES } from "./task-manager.service";

const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

// List all users for admin
export const getAdminUsers = async (token, page = 1, filters = {}) => {
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.email_filter) queryParams.append('email_filter', filters.email_filter);
  if (filters.user_id_filter) queryParams.append('user_id_filter', filters.user_id_filter);
  if (filters.kit_id_filter) queryParams.append('kit_id_filter', filters.kit_id_filter);
  if (filters.onboarding_status_filter) queryParams.append('onboarding_status_filter', filters.onboarding_status_filter);
  if (filters.enrollment_filter) queryParams.append('enrollment_filter', filters.enrollment_filter);
  if (filters.assigned_crc_filter) queryParams.append('assigned_crc_filter', filters.assigned_crc_filter);
  if (filters.aim_filter) queryParams.append('aim_filter', filters.aim_filter);
  
  const config = {
    url: `${apiServerUrl}/api/admin/users/?${queryParams.toString()}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

export const getAdminUserCrcs = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/crcs/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Export enrollment metrics to CSV
export const exportEnrollmentCSV = async (token, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.enrollment_filter) queryParams.append('enrollment_filter', params.enrollment_filter);
  if (params.aim_filter) queryParams.append('aim_filter', params.aim_filter);

  const url = `${apiServerUrl}/api/admin/users/enrollment-export/?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const errorText = await response.text();
      return { data: null, error: errorText || `HTTP error! status: ${response.status}` };
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'enrollment_export.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);

    return { data: { success: true, message: 'File downloaded successfully' }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to download file' };
  }
};

// Distinct question groups represented in the current MRR cohort
export const getMrrCohortQuestionGroups = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/mrr-cohort-question-groups/`,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const getMrrCohortExportTaskStatus = async (token, taskId) => {
  const url = `${apiServerUrl}/api/admin/users/mrr-cohort-export-run/${taskId}/status/`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to check export status' };
  }
};

export const downloadMrrCohortExportResult = async (token, taskId, suggestedFilename = null) => {
  const url = `${apiServerUrl}/api/admin/users/mrr-cohort-export-run/${taskId}/download/`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = suggestedFilename || 'mrr_cohort_export.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);

    return { data: { success: true, filename }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to download file' };
  }
};

export const runMrrCohortExport = async (token, params = {}, options = {}) => {
  const url = `${apiServerUrl}/api/admin/users/mrr-cohort-export-run/`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        question_groups: (
          Array.isArray(params.question_groups) && params.question_groups.length > 0
            ? params.question_groups
            : null
        ),
      }),
    });

    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }

    const startData = await response.json();
    const taskId = startData.task_id;
    if (!taskId) {
      return { data: null, error: 'Export did not return a task id' };
    }

    const task = createTask(taskId, TASK_TYPES.MRR_EXPORT, {
      status: startData.status || 'queued',
      title: options.title || 'MRR Cohort CSV Export',
      message: startData.message || 'MRR cohort export queued',
      actionLabel: 'Download again',
      metadata: {
        question_groups: params.question_groups || [],
      },
    });
    storeTask(task);

    return { data: { ...startData, task }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to start export' };
  }
};

// Legacy sync blob download removed — use runMrrCohortExport.

export const getAdminUserNotes = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/notes/`,
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const getNotesExportTaskStatus = async (token, taskId) => {
  const url = `${apiServerUrl}/api/admin/users/notes-export-run/${taskId}/status/`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }
    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to check export status' };
  }
};

export const downloadNotesExportResult = async (token, taskId, suggestedFilename = null) => {
  const url = `${apiServerUrl}/api/admin/users/notes-export-run/${taskId}/download/`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = suggestedFilename || 'member_notes_export.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
    return { data: { success: true, filename }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to download file' };
  }
};

export const runNotesExport = async (token, params = {}, options = {}) => {
  const url = `${apiServerUrl}/api/admin/users/notes-export-run/`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params || {}),
    });
    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }
    const startData = await response.json();
    const taskId = startData.task_id;
    if (!taskId) {
      return { data: null, error: 'Export did not return a task id' };
    }
    const task = createTask(taskId, TASK_TYPES.NOTES_EXPORT, {
      status: startData.status || 'queued',
      title: options.title || 'Updates CSV Export',
      message: startData.message || 'Updates export queued',
      actionLabel: 'Download again',
    });
    storeTask(task);
    return { data: { ...startData, task }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to start export' };
  }
};

// Get one user's detail
export const getAdminUserDetail = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Get record statistics for a user asynchronously
export const getAdminUserRecordStatistics = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/record-statistics/`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Deferred pre-screen Q&A (kept off get_user_detail critical path)
export const getAdminUserPreScreen = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/pre-screen/`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Manual session import for admin users
export const manualImportSession = async (token, userId, requestData) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/import-session/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: requestData,
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Get all sample collections across all users
export const getAdminSampleCollections = async (token, page = 1, filters = {}) => {
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('per_page', filters.per_page || 20);
  
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.intake_filter) queryParams.append('intake_filter', filters.intake_filter);
  if (filters.intake_quality) queryParams.append('intake_quality', filters.intake_quality);
  if (filters.missing_document_type) queryParams.append('missing_document_type', filters.missing_document_type);
  
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/?${queryParams.toString()}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Get sample collection status counts for dashboard metrics
export const getAdminSampleCollectionStats = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/stats/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Fire an action: create_pluto, sync_pluto, fetch_records
export const postAdminUserAction = async (token, userId, action, inputData = {}) => {
  let endpoint = `${apiServerUrl}/api/admin/users/${userId}/`;
  let config = {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    }
  };

  // Configure endpoint and request based on action
  switch(action) {
    case 'verify':
      endpoint += 'verify/';
      config.data = JSON.stringify({
        inquiry_id: inputData.inquiry_id,
        reference_id: inputData.reference_id
      });
      break;

    case 'create_pluto':
      config.data = JSON.stringify({ action: 'create_pluto', ...inputData });
      break;

    case 'sync_pluto':
      config.data = JSON.stringify({ action: 'sync_pluto' });
      break;

    case 'fetch_records':
      config.data = JSON.stringify({ action: 'fetch_records' });
      break;

    case 'import_records':
      config.data = JSON.stringify({ action: 'import_records' });
      break;

    case 'download_consent':
      config.responseType = 'blob';
      config.data = JSON.stringify({ 
        action: 'download_consent',
        document_id: inputData.document_id 
      });
      break;

    case 'get_pi_signing_url':
      config.data = JSON.stringify({ 
        action: 'get_pi_signing_url',
        document_id: inputData.document_id 
      });
      break;

    case 'refresh_consent_status':
      config.data = JSON.stringify({ action: 'refresh_consent_status' });
      break;

    case 'link_pluto':
      config.data = JSON.stringify({ 
        action: 'link_pluto',
        pluto_id: inputData.pluto_id 
      });
      break;

    case 'compare_pluto_profile':
      config.data = JSON.stringify({ action: 'compare_pluto_profile' });
      break;

    case 'preview_pluto_creation':
      config.data = JSON.stringify({ action: 'preview_pluto_creation' });
      break;

    case 'compare_pluto_link':
      config.data = JSON.stringify({ 
        action: 'compare_pluto_link',
        pluto_id: inputData.pluto_id
      });
      break;

    case 'create_fasten':
      config.data = JSON.stringify({ action: 'create_fasten' });
      break;

    case 'trigger_fasten_export':
      config.data = JSON.stringify({ action: 'trigger_fasten_export', connection_id: inputData.connection_id });
      break;

    case 'hide_from_funnel':
      config.data = JSON.stringify({ 
        action: 'hide_from_funnel',
        hidden: inputData.hidden 
      });
      break;

    case 'reconsent':
      config.data = JSON.stringify({
        action: 'reconsent',
        document_types: inputData.document_types || ['cosigned'],
      });
      break;

    case 'update_study':
      endpoint += `studies/${inputData.study_id}/`;
      config.data = JSON.stringify({ 
        status: inputData.status 
      });
      break;

    default:
      throw new Error(`Unknown action: ${action}`);
  }

  config.url = endpoint;
  const { data, error } = await callExternalApi({ config });

  // Handle binary responses (e.g., PDF downloads)
  if (config.responseType === 'blob' && !error) {
    return {
      data: new Blob([data], { type: 'application/pdf' }),
      error: null
    };
  }

  return {
    data: data || null,
    error
  };
};

export const getPatientRecords = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/records/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const getPatientRecordAttachment = async (token, userId, attachmentUrl) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/records-attachment/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify({ attachment_url: attachmentUrl }),
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

// PDF download function removed - feature discontinued

export const listSampleCollections = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/`,
    method: "GET",
    headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const { data, error } = await callExternalApi({ config });
    return {
      data: data || null,
      error,
  };
};

export const createSampleCollection = async (token, userId, phlebotomyProvider, emailOptions = {}) => {
  const requestData = {
    user_id: userId,
    phlebotomy_provider: phlebotomyProvider,
    send_email: emailOptions.send_email || false,
    custom_message: emailOptions.custom_message || '',
    email_html: emailOptions.email_html || '',
    email_subject: emailOptions.email_subject || undefined,
  };
  if (emailOptions.collection_datetime) {
    requestData.collection_datetime = emailOptions.collection_datetime;
  }
  
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/create/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(requestData),
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const previewSampleCollectionEmail = async (token, userId, studyId, phlebotomyProvider, customMessage = '') => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/preview-email/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify({
      study_id: studyId,
      phlebotomy_provider: phlebotomyProvider,
      custom_message: customMessage,
    }),
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const getSampleCollectionById = async (token, userId, sampleCollectionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${sampleCollectionId}/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const updateSampleCollection = async (token, userId, sampleCollectionId, updateData) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${sampleCollectionId}/update/`,
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(updateData),
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const getTravalabPayload = async (token, userId, sampleCollectionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${sampleCollectionId}/travalab-payload/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const resendCompensationEmail = async (token, userId, sampleCollectionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${sampleCollectionId}/compensation/resend/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify({}),
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

// Invite functions
export const sendInvite = async (token, inviteData) => {
  const config = {
    url: `${apiServerUrl}/api/admin/invites/create/`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(inviteData),
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const getAdminInvites = async (token, page = 1, perPage = 10, status = '') => {
  let url = `${apiServerUrl}/api/admin/invites/?page=${page}&per_page=${perPage}`;
  if (status) {
    url += `&status=${status}`;
  }

  const config = {
    url,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const getQuestionGroups = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/question-groups/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const resendInvite = async (token, inviteId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/invites/${inviteId}/resend/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const cancelInvite = async (token, inviteId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/invites/${inviteId}/cancel/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

// Survey Management Functions
export const getSurveyManagementData = async (token, params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.per_page) queryParams.append('per_page', params.per_page);
  if (params.question_group) queryParams.append('question_group', params.question_group);
  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);
  if (params.assigned_crc) queryParams.append('assigned_crc', params.assigned_crc);
  if (params.date_from) queryParams.append('date_from', params.date_from);
  if (params.date_to) queryParams.append('date_to', params.date_to);

  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/?${queryParams.toString()}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const getSurveyStats = async (token, params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.question_group) queryParams.append('question_group', params.question_group);
  if (params.date_from) queryParams.append('date_from', params.date_from);
  if (params.date_to) queryParams.append('date_to', params.date_to);

  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/stats/?${queryParams.toString()}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const exportSurveyData = async (token, params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.question_group) queryParams.append('question_group', params.question_group);
  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);
  if (params.date_from) queryParams.append('date_from', params.date_from);
  if (params.date_to) queryParams.append('date_to', params.date_to);
  if (params.detailed) queryParams.append('detailed', params.detailed);

  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/export/?${queryParams.toString()}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // For file downloads, we need to handle the response differently
  try {
    const response = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        data: null,
        error: errorText || `HTTP error! status: ${response.status}`,
      };
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'survey_data.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return {
      data: { success: true, message: 'File downloaded successfully' },
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      error: error.message || 'Failed to download file',
    };
  }
};

export const getSurveyQuestionGroups = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/question-groups/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const getSurveyParticipantDetail = async (token, participantId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/participant/${participantId}/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const getSurveyCrcs = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/crcs/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const generateSessionClaimLink = async (token, sessionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sessions/${sessionId}/generate-claim-link/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const updateOnboardingProgress = async (token, userId, updateData) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/onboarding-progress/`,
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(updateData),
  };

  const { data, error } = await callExternalApi({ config });
  return {
    data: data || null,
    error,
  };
};

export const cascadeDeleteUser = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/cascade-delete/`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

export const getDisenrollmentRun = async (token, userId, runId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/disenroll/${runId}/`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// FedEx Admin Services

// Get FedEx info for a sample collection
export const getFedExInfo = async (token, collectionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/collections/${collectionId}/fedex/`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Get available addresses for a sample collection
export const getCollectionAddresses = async (token, collectionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/collections/${collectionId}/fedex/addresses/`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Create FedEx labels for a sample collection
export const createFedExLabels = async (
  token,
  collectionId,
  addressOverrides = null,
  force = false,
  shipmentOverrides = null,
) => {
  const requestData = {};
  if (addressOverrides) requestData.address_overrides = addressOverrides;
  if (shipmentOverrides) requestData.shipment_overrides = shipmentOverrides;
  if (force) requestData.force = true;

  const config = {
    url: `${apiServerUrl}/api/admin/collections/${collectionId}/fedex/labels/create/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: requestData,
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Fetch raw ZPL bytes for a stored FedEx label so we can stream them to a
// Zebra printer via Browser Print.
export const getFedExLabelZpl = async (token, collectionId, direction) => {
  const url = `${apiServerUrl}/api/admin/collections/${collectionId}/fedex/labels/${direction}/zpl/`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const errorText = await response.text();
      return { data: null, error: errorText || `HTTP error! status: ${response.status}` };
    }
    const zpl = await response.text();
    return { data: zpl, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to fetch ZPL label' };
  }
};

// Download the dry-ice sticker CSV for a sample collection.
export const downloadFedExDryIceCsv = async (token, collectionId) => {
  const url = `${apiServerUrl}/api/admin/collections/${collectionId}/fedex/dry-ice-csv/`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const errorText = await response.text();
      return { data: null, error: errorText || `HTTP error! status: ${response.status}` };
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;

    let filename = `DryIceLabels_${collectionId}.csv`;
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);

    return { data: { success: true }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to download dry-ice CSV' };
  }
};

// Update FedEx tracking numbers for a sample collection
export const updateFedExTracking = async (token, collectionId, trackingNumbers) => {
  // Support both new format (object with outbound/return) and legacy format (single string)
  let requestData = {};
  
  if (typeof trackingNumbers === 'string') {
    // Legacy format - treat as outbound tracking number for backward compatibility
    requestData = { tracking_number: trackingNumbers };
  } else if (trackingNumbers && typeof trackingNumbers === 'object') {
    // New format - object with outbound_tracking_number and/or return_tracking_number
    if (trackingNumbers.outbound_tracking_number) {
      requestData.outbound_tracking_number = trackingNumbers.outbound_tracking_number;
    }
    if (trackingNumbers.return_tracking_number) {
      requestData.return_tracking_number = trackingNumbers.return_tracking_number;
    }
  }

  const config = {
    url: `${apiServerUrl}/api/admin/collections/${collectionId}/fedex/tracking/update/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: requestData,
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Refresh FedEx tracking status for a sample collection
export const refreshFedExStatus = async (token, collectionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/collections/${collectionId}/fedex/tracking/refresh/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

export const resolveFedExTracking = async (token, collectionId, direction = 'outbound') => {
  const config = {
    url: `${apiServerUrl}/api/admin/collections/${collectionId}/fedex/tracking/resolve/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { direction },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// List all FedEx shipments
export const listFedExShipments = async (token, filters = {}) => {
  const queryParams = new URLSearchParams();
  
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.user_email) queryParams.append('user_email', filters.user_email);
  if (filters.limit) queryParams.append('limit', filters.limit);
  if (filters.offset) queryParams.append('offset', filters.offset);

  const config = {
    url: `${apiServerUrl}/api/admin/fedex/shipments/?${queryParams.toString()}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Get FedEx shipments summary
export const getFedExSummary = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/fedex/summary/`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Bulk refresh FedEx tracking statuses
export const bulkRefreshFedExTracking = async (token, collectionIds) => {
  const config = {
    url: `${apiServerUrl}/api/admin/fedex/bulk-refresh/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      collection_ids: collectionIds,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// Member Funnel Services
export const getMemberFunnelData = async (token, page = 1, filters = {}) => {
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  
  if (filters.per_page) queryParams.append('per_page', filters.per_page);
  if (filters.state) queryParams.append('state', filters.state);
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.show_hidden) queryParams.append('show_hidden', filters.show_hidden);
  if (filters.tag_filter) queryParams.append('tag_filter', filters.tag_filter);
  if (filters.include_stats) queryParams.append('include_stats', 'true');
  
  const config = {
    url: `${apiServerUrl}/api/admin/member-funnel/?${queryParams.toString()}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

export const getMemberFunnelStats = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/member-funnel/stats/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

export const getFunnelStates = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/member-funnel/states/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// CRC Assignment Services

export const searchCrcUsers = async (token, query) => {
  const config = {
    url: `${apiServerUrl}/api/admin/security/users/search/?q=${encodeURIComponent(query)}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const assignCrcToUser = async (token, userId, crcUserId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/assign-crc/`,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify({ crc_user_id: crcUserId }),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const unassignCrcFromUser = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/unassign-crc/`,
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const unassignCrcFromSession = async (token, sessionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sessions/${sessionId}/unassign-crc/`,
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const assignCrcToSession = async (token, sessionId, crcUserId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sessions/${sessionId}/assign-crc/`,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify({ crc_user_id: crcUserId }),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const bulkAssignCrc = async (token, crcUserId, userIds = [], sessionIds = []) => {
  const config = {
    url: `${apiServerUrl}/api/admin/assign-crc/bulk/`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify({ crc_user_id: crcUserId, user_ids: userIds, session_ids: sessionIds }),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const bulkAssignCrcCsv = async (token, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const config = {
    url: `${apiServerUrl}/api/admin/assign-crc/csv/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

// Search users for user detail sidebar - searches by name, email, or ID
export const searchUsersForDetail = async (token, query) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/search/?search=${encodeURIComponent(query)}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};


export const downloadConsentDocument = async (token, userId, documentId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: {
      action: "download_consent",
      document_id: documentId
    },
    responseType: 'blob'
  };

  try {
    const response = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
      body: JSON.stringify(config.data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        data: null,
        error: errorText || `HTTP error! status: ${response.status}`,
      };
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `${documentId}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return {
      data: { success: true, message: 'File downloaded successfully' },
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      error: error.message || 'Failed to download file',
    };
  }
};

// Kit ID Services

// Update kit_id for a single sample collection
export const updateKitId = async (token, userId, collectionId, kitId, reason = null, confirmed = false) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${collectionId}/kit-id/`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { kit_id: kitId, reason, confirmed },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};

// --- Study Visit Tags ---

export const searchStudyVisitTags = async (token, query = '', category = '') => {
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  if (category) params.append('category', category);

  const config = {
    url: `${apiServerUrl}/api/admin/study-visit-tags/search/?${params.toString()}`,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const createStudyVisitTag = async (token, name, category, color = '') => {
  const config = {
    url: `${apiServerUrl}/api/admin/study-visit-tags/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify({ name, category, color }),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const getStudyVisitTags = async (token, userId, collectionId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${collectionId}/tags/`,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const assignStudyVisitTag = async (token, userId, collectionId, tagId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${collectionId}/tags/assign/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify({ tag_id: tagId }),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const createAndAssignStudyVisitTag = async (token, userId, collectionId, name, category, color = '') => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${collectionId}/tags/assign/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify({ name, category, color }),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const removeStudyVisitTagAssignment = async (token, userId, collectionId, assignmentId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/sample-collections/${userId}/${collectionId}/tags/${assignmentId}/`,
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

// --- Survey Export Configs (modal-driven CSV export) ---

export const getSurveyExportMetadata = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/export-metadata/`,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const listSurveyExportConfigs = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/export-configs/`,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const createSurveyExportConfig = async (token, payload) => {
  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/export-configs/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(payload),
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const updateSurveyExportConfig = async (token, configId, payload) => {
  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/export-configs/${configId}/`,
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(payload),
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const deleteSurveyExportConfig = async (token, configId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/survey-management/export-configs/${configId}/`,
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

const parseExportErrorResponse = async (response) => {
  let errorMessage = `HTTP error! status: ${response.status}`;
  let text = "";
  try {
    text = await response.text();
  } catch (_textErr) {
    return errorMessage;
  }
  try {
    const data = JSON.parse(text);
    if (data && data.error) {
      errorMessage = data.error;
    }
  } catch (_jsonErr) {
    if (text) errorMessage = text;
  }
  return errorMessage;
};

export const getSurveyExportTaskStatus = async (token, taskId) => {
  const url = `${apiServerUrl}/api/admin/survey-management/export-run/${taskId}/status/`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to check export status' };
  }
};

export const downloadSurveyExportResult = async (token, taskId, suggestedFilename = null) => {
  const url = `${apiServerUrl}/api/admin/survey-management/export-run/${taskId}/download/`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = suggestedFilename || 'survey_export.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);

    return { data: { success: true, filename }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to download file' };
  }
};

export const runSurveyExport = async (token, body, options = {}) => {
  const url = `${apiServerUrl}/api/admin/survey-management/export-run/`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body || {}),
    });

    if (!response.ok) {
      return { data: null, error: await parseExportErrorResponse(response) };
    }

    const startData = await response.json();
    const taskId = startData.task_id;
    if (!taskId) {
      return { data: null, error: 'Export did not return a task id' };
    }

    const task = createTask(taskId, TASK_TYPES.SURVEY_EXPORT, {
      status: startData.status || 'queued',
      title: options.title || 'Survey CSV Export',
      message: startData.message || 'Survey export queued',
      actionLabel: 'Download again',
      metadata: {
        filters: body?.filters || {},
        config_id: body?.config_id || null,
        has_unsaved_override: !!body?.config_override,
      },
    });
    storeTask(task);

    return { data: { ...startData, task }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to start export' };
  }
};

// Task Status Service

// Get status of an async task by task ID
export const getTaskStatus = async (token, taskId) => {
  const config = {
    url: `${apiServerUrl}/api/system/tasks/${taskId}/status/`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  return {
    data: data || null,
    error,
  };
};


export const listManualRecordUploads = async (token, userId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/manual-records/`,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const uploadManualRecord = async (token, userId, file, sourceTag, sourceNotes = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('source_tag', sourceTag);
  if (sourceNotes) formData.append('source_notes', sourceNotes);

  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/manual-records/upload/`,
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    data: formData,
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const retryManualRecordUpload = async (token, userId, uploadId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/manual-records/${uploadId}/retry/`,
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const getDatavantState = async (token, userId, { sync = false } = {}) => {
  const query = sync ? '?sync=1' : '';
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/datavant/${query}`,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const createDatavantPatient = async (token, userId, { patient = null, hipaaPages = null } = {}) => {
  const payload = {};
  if (patient) payload.patient = patient;
  if (hipaaPages) payload.hipaa_pages = hipaaPages;
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/datavant/patient/`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: payload,
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const updateDatavantPatient = async (token, userId, patientFields) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/datavant/patient/`,
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: { patient: patientFields },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const datavantFacilitySearch = async (token, userId, payload = {}) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/datavant/facility-search/`,
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const createDatavantRoi = async (token, userId, payload) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/datavant/rois/`,
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const refreshDatavantRoi = async (token, userId, roiId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/datavant/rois/${roiId}/refresh/`,
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const approveDatavantRoi = async (token, userId, roiId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/datavant/rois/${roiId}/approve/`,
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const cancelDatavantRoi = async (token, userId, roiId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/users/${userId}/datavant/rois/${roiId}/cancel/`,
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  };
  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};
